import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';

const STORAGE_KEY = 'needsAttentionManuallyCollapsed';
const SHOW_COMPLETED_KEY = 'needsAttentionShowCompleted';

export default function NeedsAttention({ onNavigate }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [showCompleted, setShowCompleted] = useState(
    () => localStorage.getItem(SHOW_COMPLETED_KEY) === 'true'
  );
  const [manuallyCollapsed, setManuallyCollapsed] = useState(
    () => localStorage.getItem(STORAGE_KEY) === 'true'
  );

  useEffect(() => {
    api.getChecklist().then(setData).catch(() => setData(null));
  }, []);

  if (!data || data.totalCount === 0) return null;

  const allDone = data.completedCount === data.totalCount;
  // Outstanding items first, then completed ones at the bottom - so what
  // still needs doing is always right at the top
  const sorted = [...data.items].sort((a, b) => (a.checked === b.checked ? 0 : a.checked ? 1 : -1));
  const visible = showCompleted ? sorted : sorted.filter((item) => !item.checked);

  const toggleManualCollapse = () => {
    const next = !manuallyCollapsed;
    setManuallyCollapsed(next);
    localStorage.setItem(STORAGE_KEY, String(next));
  };

  const toggleShowCompleted = () => {
    const next = !showCompleted;
    setShowCompleted(next);
    localStorage.setItem(SHOW_COMPLETED_KEY, String(next));
  };

  // Once everything's done, this has no more decisions to prompt - tuck it
  // into a small, low-profile pill instead of a big front-and-center card
  if (allDone) {
    return (
      <div style={styles.tuckedWrap}>
        <button style={styles.tuckedPill} onClick={() => setExpanded((e) => !e)}>
          <span style={styles.tuckedCheck}>✓</span>
          All caught up
          <span style={styles.tuckedChevron}>{expanded ? '▾' : '▸'}</span>
        </button>
        {expanded && (
          <div style={styles.tuckedList}>
            {sorted.map((item) => (
              <div key={item.id} style={styles.tuckedItem}>
                <span style={styles.tuckedItemCheck}>✓</span>
                <span style={styles.tuckedItemTitle}>{item.title}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Manually minimized by the person, even though items are still
  // outstanding - respects "I know, I'll deal with it later" without
  // forcing the full list to stay visible every time they load Overview
  if (manuallyCollapsed) {
    return (
      <div style={styles.tuckedWrap}>
        <button style={styles.minimizedPill} onClick={toggleManualCollapse}>
          <span style={styles.minimizedIcon}>📋</span>
          Needs Attention
          <span style={styles.minimizedCount}>{data.totalCount - data.completedCount}</span>
          <span style={styles.tuckedChevron}>▸</span>
        </button>
      </div>
    );
  }

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <span style={styles.headerLeft}>
          <span style={styles.title}>📋 Needs Attention</span>
        </span>
        <span style={styles.headerRight}>
          <span style={styles.progress}>{data.completedCount} of {data.totalCount} done</span>
          <button style={styles.collapseBtn} onClick={toggleManualCollapse} title="Minimize for now">
            ▾
          </button>
        </span>
      </div>

      <div style={styles.list}>
        {visible.map((item) => (
          <button
            key={item.id}
            style={styles.item}
            onClick={() => !item.checked && onNavigate?.(item)}
            disabled={item.checked}
          >
            <span style={{ ...styles.checkbox, ...(item.checked ? styles.checkboxChecked : {}) }}>
              {item.checked ? '✓' : ''}
            </span>
            <span style={styles.itemText}>
              <span style={{ ...styles.itemTitle, ...(item.checked ? styles.itemTitleDone : {}) }}>
                {item.title}
              </span>
              <span style={styles.itemDesc}>{item.description}</span>
            </span>
            {!item.checked && <span style={styles.arrow}>→</span>}
          </button>
        ))}
      </div>

      {data.completedCount > 0 && (
        <button style={styles.showCompletedToggle} onClick={toggleShowCompleted}>
          {showCompleted ? 'Hide' : 'Show'} completed ({data.completedCount})
        </button>
      )}
    </div>
  );
}

const styles = {
  tuckedWrap: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '0.85rem',
  },
  tuckedPill: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#6b7280',
    borderRadius: '999px',
    padding: '5px 12px',
    fontSize: '0.72rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  tuckedCheck: { color: '#00d4aa', fontWeight: '800' },
  tuckedChevron: { color: '#4b5563', fontSize: '0.65rem' },
  minimizedPill: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#9ca3af',
    borderRadius: '999px',
    padding: '5px 12px',
    fontSize: '0.72rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  minimizedIcon: { fontSize: '0.75rem' },
  minimizedCount: {
    background: '#fbbf24',
    color: '#0a0f1e',
    borderRadius: '999px',
    minWidth: '16px',
    height: '16px',
    padding: '0 4px',
    fontSize: '0.65rem',
    fontWeight: '800',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tuckedList: {
    marginTop: '6px',
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    padding: '0.6rem 0.8rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    width: '260px',
  },
  tuckedItem: { display: 'flex', alignItems: 'center', gap: '6px' },
  tuckedItemCheck: { color: '#00d4aa', fontSize: '0.7rem', fontWeight: '800' },
  tuckedItemTitle: {
    color: '#6b7280',
    fontSize: '0.72rem',
    textDecoration: 'line-through',
    fontFamily: "'Syne', sans-serif",
  },
  panel: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.1rem 1.25rem',
    marginBottom: '1.25rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '6px' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '10px' },
  collapseBtn: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#6b7280',
    borderRadius: '6px',
    width: '22px',
    height: '22px',
    cursor: 'pointer',
    fontSize: '0.75rem',
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: '0.92rem',
    fontWeight: '700',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
  },
  progress: {
    fontSize: '0.75rem',
    fontWeight: '600',
    color: '#9ca3af',
    fontFamily: "'DM Mono', monospace",
  },
  list: {
    marginTop: '0.9rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  item: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.65rem',
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    padding: '0.7rem 0.85rem',
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
    boxSizing: 'border-box',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    borderRadius: '5px',
    border: '1.5px solid #374151',
    color: '#0a0f1e',
    fontSize: '0.7rem',
    fontWeight: '800',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: '1px',
  },
  checkboxChecked: {
    background: '#00d4aa',
    borderColor: '#00d4aa',
  },
  itemText: { flex: 1, display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 },
  itemTitle: {
    color: '#e5e7eb',
    fontSize: '0.8rem',
    fontWeight: '600',
    fontFamily: "'Syne', sans-serif",
  },
  itemTitleDone: {
    color: '#4b5563',
    textDecoration: 'line-through',
  },
  itemDesc: {
    color: '#6b7280',
    fontSize: '0.7rem',
    lineHeight: '1.4',
  },
  arrow: { color: '#4b5563', fontSize: '0.85rem', flexShrink: 0 },
  showCompletedToggle: {
    display: 'block',
    width: '100%',
    background: 'transparent',
    border: 'none',
    color: '#4b5563',
    fontSize: '0.7rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    textAlign: 'center',
    marginTop: '0.7rem',
    padding: '0.3rem',
  },
};
